/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Box, Grid, Paper, Typography } from '@material-ui/core';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import WarningIcon from '@material-ui/icons/Warning';
import { Link, navigate } from 'gatsby';
import { FC, Fragment } from 'react';
import {
  Job,
  JobStatus,
  QuoteStatus,
  UpdateJobOutput,
} from '../../../__generated__/pgeplus-types';
import {
  TableControlsActions,
  useJobsList,
} from '../../../hooks/pge-plus/useJobsList';
import useContentMgmt from '../../../hooks/useContentMgmt';
import ROUTES from '../../../routes';
import colors from '../../../themes/main-colors';
import theme from '../../../themes/theme';
import { useIsMobile } from '../../../util/style-utils';
import Backdrop from '../../backdrop/Backdrop';
import microcopyGroupIds from '../../pge-plus-common/microcopyGroupIds';
import { DesktopView } from './table/desktop';
import { MobileView } from './table/mobile';
import AdminMenu from './AdminMenu';
import ProfilePictureMenu from './ProfilePictureMenu';
import Pagination from '../../pagination';
import { PaginationProps } from '../../pagination/Pagination';
import { PageSize } from '../../pagination/types';
import Menu from '../../menu';
import { MenuItem } from '../../menu/Menu';
import { useMutation } from '@apollo/react-hooks';
import { updateJobMutation } from '../../../queries/pge-plus/installerPortal.query';
import useUtil from '../../../hooks/pge-plus/useUtil';

export const DATE_FORMAT = 'MM/dd/yyyy';

export type JobActionsType = (job: Job) => JSX.Element;
interface Props {
  path?: string;
}

export type JobStatusType = (status: JobStatus) => string | undefined;

const JobList: FC<Props> = () => {
  const isMobile = useIsMobile();
  const { content, isContentLoading } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_INSTALLER_JOB_LISTING,
  );

  const { setErrorNotification } = useUtil();

  const {
    loading: queryLoading,
    jobs,
    pagination,
    tableControls,
    counter,
    searchValue,
    setSearchValue,
    searchParams,
  } = useJobsList();
  const loading = isContentLoading || queryLoading;

  const getMenuItems = (actionItems: Array<string>, jobId: string) => {
    const getLink = (actionItem: string) => {
      switch (actionItem) {
        case content.get('CREATE_QUOTE'):
          return ROUTES.PGE_PLUS_INSTALLER_PORTAL_COMPLETE_QUOTE.replace(
            ':jobId',
            jobId,
          );
        case content.get('CANCEL_JOB_BUTTON'):
          return ROUTES.PGE_PLUS_INSTALLER_PORTAL_JOB_CANCEL.replace(
            ':jobId',
            jobId,
          );
        case content.get('VIEW_QUOTE_BUTTON'):
          return ROUTES.PGE_PLUS_INSTALLER_PORTAL_VIEW_QUOTE.replace(
            ':jobId',
            jobId,
          );
        case content.get('REQUOTE_BUTTON'):
          return ROUTES.PGE_PLUS_INSTALLER_PORTAL_REQUOTE.replace(
            ':jobId',
            jobId,
          );
        case content.get('RESCHEDULED_OR_CANCEL_JOB_BUTTON'):
          return ROUTES.PGE_PLUS_INSTALLER_PORTAL_JOB_RESCHEDULE_OR_CANCEL.replace(
            ':jobId',
            jobId,
          );
        case content.get('SUBMIT_INSTALLATION_DATA_BUTTON'):
          return ROUTES.PGE_PLUS_INSTALLER_PORTAL_CONFIRMATION_DETAILS_AND_COST.replace(
            ':jobId',
            jobId,
          );
        case content.get('SCHEDULE_BUTTON'):
          return ROUTES.PGE_PLUS_INSTALLER_PORTAL_JOB_SCHEDULE.replace(
            ':jobId',
            jobId,
          );
        default:
          return '';
      }
    };

    return actionItems.map(actionItem => ({
      text: actionItem,
      onClick: () => navigate(getLink(actionItem)),
    }));
  };

  const [updateJob] = useMutation<{
    updateJob: UpdateJobOutput;
  }>(updateJobMutation, {
    context: {
      pgePlus: true,
      installerPortal: true,
    },
  });

  const markJobAsOpened = async (jobId: string) => {
    const { data: updatedJobData } = await updateJob({
      variables: {
        input: {
          jobId: jobId,
          isOpened: true,
        },
      },
    });
    if (!updatedJobData?.updateJob?.job?.isOpened) {
      setErrorNotification(true);
    }
  };

  const jobActions = (job: Job): JSX.Element => {
    const { id: jobId, status: jobStatus, quote } = job;
    const generateActionLinks = (
      action: string | undefined,
      link: string | undefined,
      menuItems?: Array<MenuItem>,
    ) =>
      link ? (
        <Link
          to={link}
          onClick={() => markJobAsOpened(jobId)}
          css={{
            textDecoration: 'none',
            color: colors.noirBlur,
          }}
        >
          <Box
            css={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box width="88%">
              <Typography
                css={{
                  fontSize: theme.spacing(2),
                  fontWeight: 'bold',
                  color: colors.sparkBlue,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '18px',
                }}
              >
                {action}
              </Typography>
            </Box>
            <ChevronRightIcon />
          </Box>
        </Link>
      ) : (
        <Box
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            '&>button': {
              padding: 0,
            },
          }}
        >
          <Typography
            css={{
              fontSize: theme.spacing(2),
            }}
          >
            {action}
          </Typography>
          <Menu items={menuItems!} />
        </Box>
      );

    if (jobStatus === JobStatus.ServiceCompleted) {
      // Awaiting payment
      return generateActionLinks(
        content.get('AWAITING_PAYMENT'),
        ROUTES.PGE_PLUS_INSTALLER_PORTAL_JOB_COMPLETED.replace(':jobId', jobId),
      );
    } else if (jobStatus === JobStatus.Completed) {
      // Completed. We don't have "Paid" status yet
      return generateActionLinks(
        content.get('COMPLETED_JOB'),
        ROUTES.PGE_PLUS_INSTALLER_PORTAL_JOB_COMPLETED.replace(':jobId', jobId),
      );
    } else if (jobStatus === JobStatus.Rejected) {
      // Lead declined
      return generateActionLinks(
        content.get('LEAD_DECLINED'),
        ROUTES.PGE_PLUS_INSTALLER_PORTAL_JOB_DECLINED.replace(':jobId', jobId),
      );
    } else if (quote?.status === QuoteStatus.Rejected) {
      // Quote declined : TODO
      return generateActionLinks(
        content.get('QUOTE_DECLINED'),
        ROUTES.PGE_PLUS_INSTALLER_PORTAL_VIEW_QUOTE.replace(':jobId', jobId),
      );
    } else if (
      jobStatus === JobStatus.Canceled &&
      quote?.status === QuoteStatus.Created
    ) {
      // Cancelled after quote
      return generateActionLinks(
        content.get('CANCELLED'),
        ROUTES.PGE_PLUS_INSTALLER_PORTAL_VIEW_QUOTE.replace(':jobId', jobId),
      );
    } else if (jobStatus === JobStatus.Canceled) {
      // Cancelled before quote
      return generateActionLinks(
        content.get('CANCELLED'),
        ROUTES.PGE_PLUS_INSTALLER_PORTAL_JOB_CANCELLED.replace(':jobId', jobId),
      );
    } else if (jobStatus === JobStatus.Expired) {
      // Expired
      return generateActionLinks(
        content.get('EXPIRED'),
        ROUTES.PGE_PLUS_INSTALLER_PORTAL_JOB_CANCELLED.replace(':jobId', jobId),
      );
    } else if (jobStatus === JobStatus.Created) {
      // Lead
      return generateActionLinks(
        content.get('LEAD'),
        ROUTES.PGE_PLUS_INSTALLER_PORTAL_JOB_LEAD.replace(':jobId', jobId),
      );
    } else if (jobStatus === JobStatus.Accepted && quote === null) {
      // Open Job
      return generateActionLinks(
        content.get('OPEN_JOB_STATUS'),
        undefined,
        getMenuItems(
          [content.get('CREATE_QUOTE')!, content.get('CANCEL_JOB_BUTTON')!],
          jobId,
        ),
      );
    } else if (
      jobStatus === JobStatus.Accepted &&
      (quote?.status === QuoteStatus.Created ||
        quote?.status === QuoteStatus.Accepted)
    ) {
      // Quoted
      return generateActionLinks(
        (quote.version === 2 && quote.status === QuoteStatus.Created) ||
          quote.version > 2
          ? content.get('REQUOTED')
          : content.get('QUOTED'),
        undefined,
        getMenuItems(
          [
            content.get('VIEW_QUOTE_BUTTON')!,
            content.get('REQUOTE_BUTTON')!,
            (quote.version === 2 && quote.status === QuoteStatus.Created) ||
            quote.version > 2
              ? content.get('RESCHEDULED_OR_CANCEL_JOB_BUTTON')!
              : content.get('CANCEL_JOB_BUTTON')!,
          ],
          jobId,
        ),
      );
    } else if (
      jobStatus === JobStatus.InProgress &&
      quote?.status === QuoteStatus.FinancingConfirmed &&
      !Boolean(job.serviceTime)
    ) {
      // Quote accepted
      return generateActionLinks(
        content.get('QUOTE_ACCEPTED_READY_TO_SCHEDULE'),
        undefined,
        getMenuItems(
          [
            content.get('SCHEDULE_BUTTON')!,
            content.get('REQUOTE_BUTTON')!,
            content.get('CANCEL_JOB_BUTTON')!,
          ],
          jobId,
        ),
      );
    } else if (
      jobStatus === JobStatus.InProgress &&
      quote?.status === QuoteStatus.FinancingConfirmed &&
      Boolean(job.serviceTime)
    ) {
      // Quote acceptde and job scheduled
      return generateActionLinks(
        content.get('READY_TO_INSTALL'),
        undefined,
        getMenuItems(
          [
            content.get('SUBMIT_INSTALLATION_DATA_BUTTON')!,
            content.get('REQUOTE_BUTTON')!,
            content.get('RESCHEDULED_OR_CANCEL_JOB_BUTTON')!,
          ],
          jobId,
        ),
      );
    }
    return <Box></Box>;
  };

  const paginationProps: PaginationProps = {
    count: (pagination?.totalPages || 1) * 20,
    pageSize: 20,
    pageSizes: [],
    page: pagination?.page || 1,
    visibleOnSinglePage: true,
    onPageChange: (newPage: number, newPageSize: PageSize) => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });

      tableControls(TableControlsActions.PAGINATION, newPage);
    },
  };

  return (
    <Fragment>
      {loading && <Backdrop forceOpen />}
      <Grid
        container
        css={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: theme.spacing(7),
          marginBottom: theme.spacing(7),
        }}
      >
        <Grid item xs={10}>
          <Typography
            variant="h1"
            css={{
              marginBottom: theme.spacing(2),
              ...(isMobile && { fontSize: theme.spacing(3) }),
            }}
          >
            {content.get('PGE_PLUS_INSTALLER_INTERFACE')}
          </Typography>
          <Paper
            css={{
              padding: theme.spacing(2),
              borderRadius: 0,
              boxShadow: 'none',
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                css={{
                  //ignore next code line for better readability
                  //prettier-ignore
                  ...(!isMobile && { margin: `${theme.spacing(2)}px 0 ${theme.spacing(4,)}px ${theme.spacing(2)}px` }),
                  ...(isMobile
                    ? {
                        fontSize: theme.spacing(2.3),
                        marginBottom: theme.spacing(2),
                      }
                    : { fontSize: theme.spacing(3) }),
                  color: colors.noirBlur,
                  fontFamily: 'Forma-DJR-Display',
                  letterSpacing: '1px',
                }}
              >
                {content.get('EV_CHARGING_INSTALLATION_LEADS')}
              </Typography>
              <Box>
                <AdminMenu content={content} />
                <ProfilePictureMenu content={content} />
              </Box>
            </Box>
            {isMobile ? (
              <MobileView
                content={content}
                jobs={jobs}
                jobActions={jobActions}
                tableControls={tableControls}
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                searchParams={searchParams}
              />
            ) : (
              <DesktopView
                content={content}
                jobs={jobs}
                jobActions={jobActions}
                tableControls={tableControls}
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                searchParams={searchParams}
              />
            )}

            <Grid>
              <Pagination {...paginationProps} />
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Fragment>
  );
};

export default JobList;
